import { ViewHook } from "phoenix_live_view";

import { pageLocale } from "../utils/formatters";
import { evenlySpread } from "../utils/sequence";

type FundRow = [name: string, slug: string, aum: number, fundReturn: number];

interface Point {
  name: string;
  slug: string;
  aum: number;
  fundReturn: number;
  x: number;
  y: number;
  delay: number;
}

const INTRO_MS = 1800;
const DOT_WINDOW = 0.25;
const HOVER_RADIUS = 14;

export const formatAum = (aum: number) => {
  const locale = pageLocale();

  if (locale !== "en-US") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(aum * 1e9);
  }

  if (aum >= 1000) {
    return `$${(aum / 1000).toFixed(2)}tn`;
  }

  return aum >= 100 ? `$${Math.round(aum)}bn` : `$${aum.toFixed(1)}bn`;
};

export const formatReturn = (fundReturn: number) => {
  const locale = pageLocale();

  if (locale !== "en-US") {
    return new Intl.NumberFormat(locale, {
      style: "percent",
      signDisplay: "exceptZero",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(fundReturn / 100);
  }

  return `${fundReturn > 0 ? "+" : ""}${fundReturn.toFixed(1)}%`;
};

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

class ReportFundScatter extends ViewHook {
  declare canvas: HTMLCanvasElement;
  declare tooltip: HTMLElement;
  declare points: Point[];
  declare hovered?: Point;
  declare progress: number;
  declare startedAt: number;
  declare frame?: number;
  declare visible: boolean;
  declare loading?: Promise<void>;
  declare width: number;
  declare height: number;
  declare resizeObserver: ResizeObserver;
  declare visibilityObserver: IntersectionObserver;

  mounted() {
    this.canvas = this.el.querySelector<HTMLCanvasElement>('[data-part="canvas"]')!;
    this.tooltip = this.el.querySelector<HTMLElement>('[data-part="tooltip"]')!;
    this.points = [];
    this.progress = 0;
    this.visible = false;
    this.width = 0;
    this.height = 0;

    this.tick = this.tick.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onClick = this.onClick.bind(this);

    this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
    this.resizeObserver.observe(this.canvas);

    this.visibilityObserver = new IntersectionObserver(this.onVisibility.bind(this), {
      threshold: 0.25,
    });
    this.visibilityObserver.observe(this.el);

    this.canvas.addEventListener("pointermove", this.onPointerMove);
    this.canvas.addEventListener("pointerleave", this.onPointerLeave);
    this.canvas.addEventListener("click", this.onClick);
  }

  private async load() {
    const response = await fetch(this.el.dataset.src!);
    const { funds } = (await response.json()) as { funds: FundRow[] };

    this.points = funds.map((row, index) => this.toPoint(row, index));
  }

  destroyed() {
    this.stop();
    this.resizeObserver.disconnect();
    this.visibilityObserver.disconnect();

    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerleave", this.onPointerLeave);
    this.canvas.removeEventListener("click", this.onClick);
  }

  private toPoint([name, slug, aum, fundReturn]: FundRow, index: number): Point {
    const { xMin, xMax, yMin, yMax } = this.el.dataset;
    const x = (Math.log10(aum) - Number(xMin)) / (Number(xMax) - Number(xMin));
    const y = (fundReturn - Number(yMin)) / (Number(yMax) - Number(yMin));

    return { name, slug, aum, fundReturn, x, y, delay: x * 0.6 + evenlySpread(index) * 0.15 };
  }

  private onVisibility(entries: IntersectionObserverEntry[]) {
    this.visible = entries.some((entry) => entry.isIntersecting);

    if (this.visible) {
      void this.show();
    } else {
      this.stop();
      this.progress = 0;
      this.hovered = undefined;
      this.renderTooltip();
    }
  }

  private async show() {
    this.loading ??= this.load();
    await this.loading;

    if (!this.visible || this.frame !== undefined) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.progress = 1;
      this.draw();
      return;
    }

    this.startedAt = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  private stop() {
    if (this.frame !== undefined) {
      cancelAnimationFrame(this.frame);
      this.frame = undefined;
    }
  }

  private tick(now: number) {
    this.progress = Math.min(1, (now - this.startedAt) / INTRO_MS);
    this.draw();

    this.frame = this.progress < 1 ? requestAnimationFrame(this.tick) : undefined;
  }

  private onResize() {
    const { width, height } = this.canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;

    this.width = width;
    this.height = height;
    this.canvas.width = Math.round(width * ratio);
    this.canvas.height = Math.round(height * ratio);
    this.canvas.getContext("2d")?.setTransform(ratio, 0, 0, ratio, 0, 0);

    this.draw();
  }

  private draw() {
    const context = this.canvas.getContext("2d");

    if (!context) {
      return;
    }

    const styles = getComputedStyle(this.el);

    context.clearRect(0, 0, this.width, this.height);
    context.fillStyle = styles.getPropertyValue("--report-bar").trim();
    context.strokeStyle = styles.getPropertyValue("--report-surface").trim();
    context.lineWidth = 0.5;

    for (const point of this.points) {
      const local = Math.min(1, (this.progress - point.delay) / DOT_WINDOW);

      if (local > 0) {
        const eased = easeOutCubic(local);

        context.globalAlpha = 0.9 * eased;
        context.beginPath();
        context.arc(
          point.x * this.width,
          (1 - point.y) * this.height,
          2.5 * eased,
          0,
          Math.PI * 2,
        );
        context.fill();
        context.stroke();
      }
    }

    if (this.hovered) {
      context.globalAlpha = 1;
      context.strokeStyle = styles.getPropertyValue("--report-ink").trim();
      context.lineWidth = 2;
      context.beginPath();
      context.arc(
        this.hovered.x * this.width,
        (1 - this.hovered.y) * this.height,
        5,
        0,
        Math.PI * 2,
      );
      context.fill();
      context.stroke();
    }

    context.globalAlpha = 1;
  }

  private nearest(clientX: number, clientY: number) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    let best: Point | undefined;
    let bestDistance = HOVER_RADIUS;

    for (const point of this.points) {
      const distance = Math.hypot(
        point.x * this.width - mouseX,
        (1 - point.y) * this.height - mouseY,
      );

      if (distance < bestDistance) {
        best = point;
        bestDistance = distance;
      }
    }

    return best;
  }

  private onPointerMove(event: PointerEvent) {
    const point = this.progress === 1 ? this.nearest(event.clientX, event.clientY) : undefined;

    if (point === this.hovered) {
      return;
    }

    this.hovered = point;
    this.canvas.style.cursor = point ? "pointer" : "";
    this.renderTooltip();
    this.draw();
  }

  private onPointerLeave() {
    this.hovered = undefined;
    this.canvas.style.cursor = "";
    this.renderTooltip();
    this.draw();
  }

  private onClick() {
    if (this.hovered) {
      window.location.href = `${this.el.dataset.fundPath}/${this.hovered.slug}`;
    }
  }

  private renderTooltip() {
    if (!this.hovered) {
      this.tooltip.hidden = true;
      return;
    }

    const name = document.createElement("strong");
    name.className = "block font-semibold";
    name.textContent = this.hovered.name;

    const figures = document.createElement("span");
    figures.className = "block tabular-nums text-report-muted";
    figures.textContent = this.el.dataset
      .managesFormat!.replace("%{aum}", formatAum(this.hovered.aum))
      .replace("%{return}", formatReturn(this.hovered.fundReturn));

    this.tooltip.replaceChildren(name, figures);
    this.tooltip.style.left = `${this.hovered.x * this.width}px`;
    this.tooltip.style.top = `${(1 - this.hovered.y) * this.height - 12}px`;
    this.tooltip.hidden = false;
  }
}

export default ReportFundScatter;
